import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import type { Asset, AssetFolder, PartnerAssetsView, PartnerOwnAsset } from "../../shared/types";
import { useNative } from "../../shared/phone/media";
import { usePartnerAuth } from "../context/PartnerAuthContext";
import { partnerApi, PartnerApiError } from "../lib/api";
import {
  canPreview,
  extOf,
  metaSub,
  ownAssetToAsset,
  ownCanPreview,
  ownMetaSub,
  typeLabel,
  useOwnAssets,
  usePreview,
} from "../lib/assets";
import { AssetsNative } from "./AssetsNative";

/**
 * The partner portal assets view (node P4): the owner-shared asset library the
 * signed-in partner may use to promote, for the selected program. Read-only and
 * SECURITY-CRITICAL: every folder and asset comes from GET /api/partner/assets,
 * which returns ONLY the partner-shared subset (S4's one share predicate), scoped
 * to the product resolved from the session enrollment. An unshared asset is never
 * listed and never downloadable. Preview + Download stream through the
 * partner-authorized route; swipe copy is copied to the clipboard. Honest empty
 * state; polished light + dark.
 */

// ── icons ──────────────────────────────────────────────────────────────────

function FolderIcon() {
  return (
    <svg className="fsvg" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );
}

function ThumbGlyph({ type }: { type: Asset["type"] }) {
  if (type === "video") {
    return (
      <svg className="tg" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M9 8l7 4-7 4z" />
      </svg>
    );
  }
  if (type === "image") {
    return (
      <svg className="tg" viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3.5" y="5" width="17" height="14" rx="2" />
        <circle cx="9" cy="10" r="1.6" />
        <path d="M5 17l4.5-4 3.5 3 3-2.5L19 17" />
      </svg>
    );
  }
  return (
    <svg className="tg" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 3h7l4 4v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M14 3v4h4" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg className="s-ic" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="M20.5 20.5l-4-4" />
    </svg>
  );
}

// ── preview lightbox: fetches the authorized bytes, renders inline ───────────

function PreviewModal({
  asset,
  downloadUrl,
  onClose,
}: {
  asset: Asset;
  downloadUrl: string;
  onClose: () => void;
}) {
  const { url, state, msg } = usePreview(asset, downloadUrl);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isPdf = asset.contentType === "application/pdf" || extOf(asset) === "pdf";

  return (
    <div
      className="scrim open"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal wide" role="dialog" aria-modal="true" aria-label={`Preview ${asset.name}`}>
        <div className="mhead">
          <h3>{asset.name}</h3>
          <button type="button" className="mx" aria-label="Close" onClick={onClose}>
            &#10005;
          </button>
        </div>
        <div className="mbody preview">
          {asset.type === "copy" && <div className="pv-copy">{asset.text}</div>}
          {asset.type !== "copy" && state === "loading" && (
            <div className="pv-note">Loading preview...</div>
          )}
          {asset.type !== "copy" && state === "error" && (
            <div className="pv-note">
              {msg} You can still download it to view.
              <div className="pv-actions">
                <a className="btn ghost sm" href={downloadUrl}>
                  Download
                </a>
              </div>
            </div>
          )}
          {state === "ready" && url && asset.type === "image" && (
            <img className="pv-img" src={url} alt={asset.name} />
          )}
          {state === "ready" && url && asset.type === "video" && (
            <video className="pv-video" src={url} controls>
              <track kind="captions" />
            </video>
          )}
          {state === "ready" && url && asset.type === "file" && isPdf && (
            <iframe className="pv-frame" src={url} title={asset.name} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── "Added by you": the partner's own uploads (node P4) ──────────────────────

/**
 * The partner's OWN uploaded assets, in a section clearly separate from the
 * owner-shared library above. Upload (real, streamed to R2 with live progress),
 * download, preview (reuses the shared PreviewModal), and delete (inline confirm,
 * never a browser dialog). Only the owning partner ever sees these, and only they
 * can delete them. Honest empty + error states.
 */
function OwnAssets({
  programId,
  onPreview,
}: {
  programId: string;
  onPreview: (asset: PartnerOwnAsset) => void;
}) {
  const { assets, error, uploading, progress, busyId, upload, remove } = useOwnAssets(programId);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setConfirmId(null);
  }, [programId]);

  const onPick = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (file) void upload(file);
    },
    [upload],
  );

  const onDelete = useCallback(
    async (id: string) => {
      if (await remove(id)) setConfirmId(null);
    },
    [remove],
  );

  const list = assets ?? [];

  return (
    <section className="own-assets">
      <div className="own-head">
        <h3>Added by you</h3>
        <span className="own-sub">Your own creatives. Only you can see these.</span>
      </div>

      {error && <div className="own-error">{error}</div>}

      <div className="assets">
        {list.map((a) => (
          <div className="asset" key={a.id}>
            <div className="thumb">
              <span className="ty">{typeLabel(a.type)}</span>
              <ThumbGlyph type={a.type} />
            </div>
            <div className="meta">
              <div className="an" title={a.name}>
                {a.name}
              </div>
              <div className="as">{ownMetaSub(a)}</div>
            </div>
            <div className="acts">
              {ownCanPreview(a) && (
                <button type="button" className="btn ghost sm" onClick={() => onPreview(a)}>
                  Preview
                </button>
              )}
              <a className="btn sm" href={partnerApi.myAssetDownloadUrl(a.id)}>
                Download
              </a>
              {confirmId === a.id ? (
                <>
                  <button
                    type="button"
                    className="btn ghost sm danger"
                    disabled={busyId === a.id}
                    onClick={() => onDelete(a.id)}
                  >
                    {busyId === a.id ? "Removing..." : "Confirm"}
                  </button>
                  <button
                    type="button"
                    className="btn ghost sm"
                    disabled={busyId === a.id}
                    onClick={() => setConfirmId(null)}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn ghost sm danger"
                  onClick={() => setConfirmId(a.id)}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}

        <button
          type="button"
          className="asset add own-add"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? (
            <span className="own-uploading">
              <span className="own-bar">
                <span className="own-bar-fill" style={{ width: `${progress}%` }} />
              </span>
              <span>Uploading {progress}%</span>
            </span>
          ) : (
            <>
              <span className="plus" aria-hidden="true">
                +
              </span>
              <span>Add your own asset</span>
            </>
          )}
        </button>
        <input ref={fileRef} type="file" className="own-file" onChange={onPick} />

        {assets !== null && list.length === 0 && !uploading && (
          <div className="assets-none">You have not added anything yet.</div>
        )}
      </div>
    </section>
  );
}

// ── main ─────────────────────────────────────────────────────────────────────

export function AssetsView() {
  const { currentMembership } = usePartnerAuth();
  const programId = currentMembership?.programId ?? null;

  const [view, setView] = useState<PartnerAssetsView | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [openFolder, setOpenFolder] = useState<{ id: string; name: string } | null>(null);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [preview, setPreview] = useState<{ asset: Asset; url: string } | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const copyTimer = useRef<number | null>(null);
  const native = useNative();
  const { folderId } = useParams<{ folderId: string }>();

  const load = useCallback(async (pid: string) => {
    setError(null);
    try {
      const data = await partnerApi.assets(pid);
      setView(data);
    } catch (err) {
      setView(null);
      setError(
        err instanceof PartnerApiError
          ? err.message
          : "We could not load your assets. Try again in a moment.",
      );
    }
  }, []);

  useEffect(() => {
    if (!programId) return;
    // Reset the browse state when the selected program changes.
    setOpenFolder(null);
    setQuery("");
    void load(programId);
  }, [programId, load]);

  useEffect(() => () => void (copyTimer.current && window.clearTimeout(copyTimer.current)), []);

  const copyToClipboard = useCallback(async (asset: Asset) => {
    try {
      await navigator.clipboard.writeText(asset.text ?? "");
      setCopiedId(asset.id);
      if (copyTimer.current) window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(
        () => setCopiedId((c) => (c === asset.id ? null : c)),
        1600,
      );
    } catch {
      // Clipboard can be unavailable (permissions, insecure context). Preview
      // still shows the full text to copy by hand.
    }
  }, []);

  const folders: AssetFolder[] = view?.folders ?? [];
  const assets: Asset[] = view?.assets ?? [];
  const productName = view?.productName ?? currentMembership?.productName ?? "your program";

  const q = query.trim().toLowerCase();
  const searching = q.length > 0;
  const visibleFolderIds = useMemo(() => new Set(folders.map((f) => f.id)), [folders]);

  // What renders: searching = matching assets across the whole shared set; a
  // folder is open = that folder's assets; root = assets not inside a visible
  // folder (an own-shared asset in a private folder detaches to the root, since
  // the private folder is never returned). Folders show only at root.
  const shownAssets = useMemo(() => {
    if (searching) return assets.filter((a) => a.name.toLowerCase().includes(q));
    if (openFolder) return assets.filter((a) => a.folderId === openFolder.id);
    return assets.filter((a) => a.folderId === null || !visibleFolderIds.has(a.folderId));
  }, [assets, searching, q, openFolder, visibleFolderIds]);

  const showFolders = !searching && !openFolder;
  const isEmpty = folders.length === 0 && assets.length === 0;

  const downloadUrl = useCallback(
    (asset: Asset) => (programId ? partnerApi.assetDownloadUrl(asset.id, programId) : "#"),
    [programId],
  );

  const onPreviewOwn = useCallback((asset: PartnerOwnAsset) => {
    setPreview({ asset: ownAssetToAsset(asset), url: partnerApi.myAssetDownloadUrl(asset.id) });
  }, []);

  if (native && programId) {
    return (
      <AssetsNative
        view={view}
        error={error}
        onRetry={() => void load(programId)}
        programId={programId}
        folderId={folderId ?? null}
      />
    );
  }

  // ── states: error, loading, then the library ──

  if (error && !view) {
    return (
      <div className="content">
        <div className="es assets-error">
          <div className="es-ic" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M12 8v5M12 16.5v.5" />
              <circle cx="12" cy="12" r="9" />
            </svg>
          </div>
          <h3>Something went wrong</h3>
          <p>{error}</p>
          <button type="button" className="btn ghost" onClick={() => programId && load(programId)}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!view) {
    return (
      <div className="content">
        <div className="home-loading">
          <div className="ps-dot" aria-hidden="true" />
          <span>Loading your assets...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="content app-view">
      <div className="info-note">
        <span className="i" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          </svg>
        </span>
        <div>
          <b>Assets from {productName}.</b> Use these to promote, or add your own.
        </div>
      </div>

      {isEmpty ? (
        <div className="es">
          <div className="es-ic" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <rect x="3.5" y="4.5" width="17" height="15" rx="2.4" />
              <path d="M3.5 9.5h17M8.5 4.5v5" />
            </svg>
          </div>
          <h3>No assets shared yet</h3>
          <p>
            {productName} has not added creatives yet. You can still promote with your link, or add
            your own material.
          </p>
        </div>
      ) : (
        <div className="assetbox">
          <div className="assetnav">
            <div className="crumb">
              <button
                type="button"
                className="cr-root"
                onClick={() => {
                  setOpenFolder(null);
                  setQuery("");
                }}
              >
                All assets
              </button>
              {openFolder && !searching && (
                <>
                  <span className="cr-sep" aria-hidden="true">
                    /
                  </span>
                  <span className="cr-cur">{openFolder.name}</span>
                </>
              )}
            </div>
            <div className={`searchbox ${searchOpen || searching ? "open" : ""}`}>
              <button
                type="button"
                className="s-btn"
                aria-label="Search assets"
                onClick={() => {
                  setSearchOpen(true);
                  searchRef.current?.focus();
                }}
              >
                <SearchIcon />
              </button>
              <input
                ref={searchRef}
                className="asset-search"
                placeholder="Search assets"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => {
                  if (!query.trim()) setSearchOpen(false);
                }}
              />
            </div>
          </div>

          <div className="assets">
            {showFolders &&
              folders.map((f) => (
                <div
                  key={f.id}
                  className="folder-row"
                  role="button"
                  tabIndex={0}
                  onClick={() => setOpenFolder({ id: f.id, name: f.name })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOpenFolder({ id: f.id, name: f.name });
                    }
                  }}
                >
                  <span className="fic">
                    <FolderIcon />
                  </span>
                  <div className="fmeta">
                    <div className="fn">{f.name}</div>
                    <div className="fc">
                      {f.itemCount} {f.itemCount === 1 ? "item" : "items"}
                    </div>
                  </div>
                  <span className="fchev" aria-hidden="true">
                    &#8250;
                  </span>
                </div>
              ))}

            {shownAssets.map((a) => (
              <div className="asset" key={a.id}>
                <div className={`thumb ${a.type === "copy" ? "c" : ""}`}>
                  <span className="ty">{typeLabel(a.type)}</span>
                  {a.type === "copy" ? (
                    <span className="ctext">{a.text}</span>
                  ) : (
                    <ThumbGlyph type={a.type} />
                  )}
                </div>
                <div className="meta">
                  <div className="an" title={a.name}>
                    {a.name}
                  </div>
                  <div className="as">{metaSub(a)}</div>
                </div>
                <div className="acts">
                  {canPreview(a) && (
                    <button
                      type="button"
                      className="btn ghost sm"
                      onClick={() => setPreview({ asset: a, url: downloadUrl(a) })}
                    >
                      Preview
                    </button>
                  )}
                  {a.type === "copy" ? (
                    <button type="button" className="btn sm" onClick={() => copyToClipboard(a)}>
                      {copiedId === a.id ? "Copied" : "Copy"}
                    </button>
                  ) : (
                    <a className="btn sm" href={downloadUrl(a)}>
                      Download
                    </a>
                  )}
                </div>
              </div>
            ))}

            {/* Honest in-context empty states */}
            {!showFolders && shownAssets.length === 0 && (
              <div className="assets-none">
                {searching ? `No assets match "${query.trim()}".` : "No assets in this folder yet."}
              </div>
            )}
          </div>
        </div>
      )}

      {programId && <OwnAssets programId={programId} onPreview={onPreviewOwn} />}

      {preview && (
        <PreviewModal
          asset={preview.asset}
          downloadUrl={preview.url}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}
